import { LanguageOption, PageOption } from "#/components/dynamic-content/util/dynamicContent"
import { reportErrorMessage } from "../../../utils/ErrorReporting"
import { Asset, EntrySkeletonType } from "contentful"
import {
  TypeCalendlyLinkSkeleton,
  TypePhoneSupportNumberSkeleton,
  TypeTalkToCarrotCountryV2Skeleton,
  TypeTalkToCarrotLanguagePageSkeleton,
  TypeTalkToCarrotLanguageSkeleton,
  TypeTalkToCarrotSendAMessageSkeleton,
  TypeTilePageSkeleton,
  TypeTileSkeleton
} from "#/types/contentful"
import slugify from "slugify"
import { toCondition } from "./conditionUtils"
import { Condition } from "#/services/contentful/conditional-gate"
import {
  ContentfulOrderTag,
  Journey,
  Order,
  ReadAndLearnTag,
  TalkToCarrotTag,
  journeyOrderMapping
} from "./journeyOrderMapping"
import { EntryWithoutUnresolvableLinks, SortableContentEntry, isCategory, isTile } from "#/utils/ContentTypes"
import { Tag } from "#/components/read/shared/CategoryMapper"

enum TileNextActionType {
  TILE_PAGE = "tilePage",
  LANGUAGE_PAGE = "talkToCarrotLanguagePage",
  CALENDLY_LINK = "calendlyLink",
  TALK_TO_CARROT_SEND_A_MESSAGE = "talkToCarrotSendAMessage",
  PHONE_SUPPORT_NUMBER = "phoneSupportNumber"
}

type TileNextActionSkeleton =
  | TypeTilePageSkeleton
  | TypeCalendlyLinkSkeleton
  | TypeTalkToCarrotSendAMessageSkeleton
  | TypeTalkToCarrotLanguagePageSkeleton
  | TypePhoneSupportNumberSkeleton

export type TileNextActionEntry = EntryWithoutUnresolvableLinks<TileNextActionSkeleton>

type EntryOrLink = {
  sys: { type: "Entry" | "Link" }
}

/* The check in fieldIsValid doesn't seem necessary, as the types throughout the project seem to gurantee that the
 * values passed into this are already of type Entry and not Link. Since this function is used so extensively, I
 * decided to keep it as is and widen the type of the argument, but it may be possible to remove it entirely. */

const fieldIsValid = (field: EntryOrLink): field is EntryWithoutUnresolvableLinks<EntrySkeletonType> => {
  return field && field.sys.type !== "Link"
}

const getTileTitle = (tile: EntryWithoutUnresolvableLinks<TypeTileSkeleton>): string => {
  if (fieldIsValid(tile)) {
    const {
      fields: { display }
    } = tile
    if (fieldIsValid(display)) {
      const {
        fields: { title }
      } = display
      return title
    }
  }
  return null
}

const getTileSlug = (tile: EntryWithoutUnresolvableLinks<TypeTileSkeleton>): string => {
  if (fieldIsValid(tile)) {
    const {
      fields: { display }
    } = tile
    if (fieldIsValid(display)) {
      const {
        fields: { slug }
      } = display
      return slug
    }
  }
  return null
}

const getLanguageName = (language: EntryWithoutUnresolvableLinks<TypeTalkToCarrotLanguageSkeleton>): string => {
  if (fieldIsValid(language)) {
    const {
      fields: { name }
    } = language
    return name
  }
  return null
}

const getPathForTile = ({
  tile,
  baseUrl
}: {
  tile: EntryWithoutUnresolvableLinks<TypeTileSkeleton>
  baseUrl: string
}): string => {
  const tileSlug = getTileSlug(tile)
  const slug = tileSlug ?? "unknown"

  return `${baseUrl}/${slug}`
}

const getPathForLanguage = ({
  language,
  baseUrl
}: {
  language: EntryWithoutUnresolvableLinks<TypeTalkToCarrotLanguageSkeleton>
  baseUrl: string
}): string => {
  const languageName = getLanguageName(language)
  const slug = languageName ? slugify(languageName).toLowerCase() : "unknown"
  return `${baseUrl}/${slug}`
}

const getNextTileAction = (tile: EntryWithoutUnresolvableLinks<TypeTileSkeleton>): TileNextActionEntry => {
  const {
    fields: { nextAction }
  } = tile
  return nextAction
}

const getNextLanguageAction = (language: EntryWithoutUnresolvableLinks<TypeTalkToCarrotLanguageSkeleton>) => {
  const {
    fields: { nextAction }
  } = language
  return nextAction
}

function isTileNextActionType(nextAction: TileNextActionEntry, tileNextActionType: TileNextActionType) {
  return nextAction && nextAction.sys.contentType.sys.id === tileNextActionType
}

export const isTilePage = (
  nextAction: TileNextActionEntry
): nextAction is EntryWithoutUnresolvableLinks<TypeTilePageSkeleton> =>
  isTileNextActionType(nextAction, TileNextActionType.TILE_PAGE)

export const isAppointmentLink = (
  nextAction: TileNextActionEntry
): nextAction is EntryWithoutUnresolvableLinks<TypeCalendlyLinkSkeleton> =>
  isTileNextActionType(nextAction, TileNextActionType.CALENDLY_LINK)

export const isLanguagePage = (
  nextAction: TileNextActionEntry
): nextAction is EntryWithoutUnresolvableLinks<TypeTalkToCarrotLanguagePageSkeleton> =>
  isTileNextActionType(nextAction, TileNextActionType.LANGUAGE_PAGE)

export const isPhoneSupportNumber = (
  nextAction: TileNextActionEntry
): nextAction is EntryWithoutUnresolvableLinks<TypePhoneSupportNumberSkeleton> =>
  isTileNextActionType(nextAction, TileNextActionType.PHONE_SUPPORT_NUMBER)

export const isTalkToCarrotSendAMessage = (
  nextAction: TileNextActionEntry
): nextAction is EntryWithoutUnresolvableLinks<TypeTalkToCarrotSendAMessageSkeleton> =>
  isTileNextActionType(nextAction, TileNextActionType.TALK_TO_CARROT_SEND_A_MESSAGE)

const getNextActionType = (nextAction: TileNextActionEntry): TileNextActionType => {
  if (nextAction) {
    const {
      sys: {
        contentType: {
          sys: { id }
        }
      }
    } = nextAction
    return Object.values(TileNextActionType).find((type) => type === id)
  }
  return null
}

const getIconUrl = (icon: Asset<"WITHOUT_UNRESOLVABLE_LINKS">): string => {
  if (icon) {
    const {
      fields: { file }
    } = icon
    return file ? file.url : null
  }
  return null
}

const getTTCLocationOption = ({
  fields: { country, page }
}: EntryWithoutUnresolvableLinks<TypeTalkToCarrotCountryV2Skeleton>): PageOption => {
  if (fieldIsValid(country) && fieldIsValid(page)) {
    const {
      fields: { name, alpha2CountryCode }
    } = country
    const {
      sys: { id }
    } = page
    return {
      slug: slugify(name).toLowerCase(),
      label: name,
      value: alpha2CountryCode,
      pageId: id
    }
  } else {
    return null
  }
}

const getTTCLanguageOption = ({
  fields: { name, iso639LanguageCode, nextAction },
  sys: { id }
}: EntryWithoutUnresolvableLinks<TypeTalkToCarrotLanguageSkeleton>): LanguageOption => {
  if (fieldIsValid(nextAction)) {
    return {
      label: name,
      value: iso639LanguageCode,
      nextAction: nextAction,
      entryId: id
    }
  } else {
    return null
  }
}

const END_ACTIONS = [
  TileNextActionType.CALENDLY_LINK,
  TileNextActionType.TALK_TO_CARROT_SEND_A_MESSAGE,
  TileNextActionType.LANGUAGE_PAGE,
  TileNextActionType.PHONE_SUPPORT_NUMBER
]

const isTileDeadEnd = (
  tile: EntryWithoutUnresolvableLinks<TypeTileSkeleton>,
  conditionsCheckFn: (conditions: Condition[]) => boolean
): boolean => {
  const action = getNextTileAction(tile)
  const type = getNextActionType(action)
  if (END_ACTIONS.includes(type)) {
    return false
  } else if (isTilePage(action)) {
    const {
      fields: { tiles }
    } = action

    const userMeetsConditions = ({
      fields: { conditions }
    }: EntryWithoutUnresolvableLinks<TypeTileSkeleton>): boolean => {
      return !conditions || conditionsCheckFn(conditions.map(toCondition))
    }
    const tileIsNotDeadend = (tile: EntryWithoutUnresolvableLinks<TypeTileSkeleton>): boolean =>
      !isTileDeadEnd(tile, conditionsCheckFn)

    const allowedTilesWithEndActions = tiles.filter(userMeetsConditions).filter(tileIsNotDeadend)
    return allowedTilesWithEndActions.length === 0
  } else if (type !== null) {
    reportErrorMessage(`Unexpected next action type for Talk to Carrot tile. NextActionType: ${type}`)
  }
  // null is allowed.  Those are our intentionally disabled tiles.
  return true
}

const CALENDLY_PRIMARY_COLOR = "1A1A1A"

const getAllTagIds = (entry: SortableContentEntry): string[] => {
  if (isCategory(entry)) {
    return entry.tags.map((tag: Tag) => tag.id)
  } else {
    return entry.metadata.tags.map((tag) => tag.sys.id)
  }
}

const getOrderTagId = (entry: SortableContentEntry): ContentfulOrderTag => {
  const tagId = getAllTagIds(entry).find((tagId) =>
    Object.values(ContentfulOrderTag).includes(tagId as ContentfulOrderTag)
  )
  return tagId as ContentfulOrderTag
}

const getJourneySpecificOrder = (journey: Journey | "DEFAULT", entry: SortableContentEntry): number => {
  const tagId = getOrderTagId(entry)

  if (isCategory(entry)) {
    return journeyOrderMapping[journey][Order.READ_AND_LEARN].indexOf(tagId as ReadAndLearnTag)
  } else if (isTile(entry)) {
    return journeyOrderMapping[journey][Order.TALK_TO_CARROT].indexOf(tagId as TalkToCarrotTag)
  }
  return null
}

const sortByJourneySpecificOrder = (
  journey: Journey | "DEFAULT",
  a: SortableContentEntry,
  b: SortableContentEntry
): number => {
  const aOrder = getOrderTagId(a) ? getJourneySpecificOrder(journey, a) : Infinity
  const bOrder = getOrderTagId(b) ? getJourneySpecificOrder(journey, b) : Infinity
  return aOrder - bOrder
}

export {
  TileNextActionType,
  getTileTitle,
  getLanguageName,
  getPathForTile,
  getPathForLanguage,
  fieldIsValid,
  getNextTileAction,
  getNextLanguageAction,
  getIconUrl,
  getNextActionType,
  getTTCLanguageOption,
  isTileDeadEnd,
  getTTCLocationOption,
  CALENDLY_PRIMARY_COLOR,
  getAllTagIds,
  getOrderTagId,
  getJourneySpecificOrder,
  sortByJourneySpecificOrder
}
